import styled from 'styled-components';

import { Metrics } from 'components/insights/constants';
import Card from 'components/insights/layout/Card';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

import { InboundStatus } from '../constants';

import InboundReferralHighlightMetrics from './InboundReferralHighlightMetrics';
import InboundReferralHighlightsHeader from './InboundReferralHighlightsHeader';
import InboundReferralStatusDonutChart from './InboundReferralStatusDonutChart';

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

export default function InboundReferralHighlightsCard({ loading, rollups }: Props) {
  const overall = rollups.overall.metrics;
  const acceptanceRate = overall.getCurrentDataPoint(Metrics.ACCEPTANCE_RATE, 0);
  const denialRate = overall.getCurrentDataPoint(Metrics.DENIAL_RATE, 0);
  const selectedRate = overall.getCurrentDataPoint(Metrics.SELECTED_RATE, 0);
  const notSelectedRate = overall.getCurrentDataPoint(Metrics.NOT_SELECTED_RATE, 0);
  const admittedRate = overall.getCurrentDataPoint(Metrics.ADMISSION_RATE, 0);
  const avgTimeToDecisionSeconds = overall.getCurrentDataPoint(Metrics.TIME_TO_FACILITY_DECISION_SECONDS_AVERAGE, 0);

  // Real per-status counts from the API (row grain, same denominator as the rates). The donut is the
  // determination funnel — Accepted / Denied / Pending — so every building lands in exactly one
  // slice. Selected / Not Selected are shown as rate tiles, not slices (they cut across all three
  // determination buckets), so their counts feed only the tile subtitles below.
  const acceptedCount = overall.getCurrentDataPoint(Metrics.ACCEPTED_COUNT, 0) ?? 0;
  const deniedCount = overall.getCurrentDataPoint(Metrics.DENIED_COUNT, 0) ?? 0;
  const pendingCount = overall.getCurrentDataPoint(Metrics.PENDING_COUNT, 0) ?? 0;
  const selectedCount = overall.getCurrentDataPoint(Metrics.SELECTED_COUNT, 0) ?? 0;
  const notSelectedCount = overall.getCurrentDataPoint(Metrics.NOT_SELECTED_COUNT, 0) ?? 0;
  const admittedCount = overall.getCurrentDataPoint(Metrics.ADMITTED_COUNT, 0) ?? 0;
  // Denominator for the selected / not-selected rate tiles: closed referrals (selected + not
  // selected). Read from the API's closed_count so the landing tiles and the drill headers, which
  // both say "total closed", can't drift apart.
  const closedCount = overall.getCurrentDataPoint(Metrics.CLOSED_COUNT, 0) ?? 0;
  // Sample for the avg time to facility decision: referrals with a decision (accepted or denied).
  const decidedCount = acceptedCount + deniedCount;

  // "Total received" denominator for the rate tiles: read referral:count from the API — the same
  // metric the drill headers use — so the landing tiles and the drills can't drift. It's a
  // facility-grain COUNT(*) (not distinct), the same denominator the determination rates divide by.
  const totalReceived = overall.getCurrentDataPoint(Metrics.REFERRAL_COUNT, 0) ?? 0;

  // The donut is the determination funnel, so its total is the sum of its own slices (accepted +
  // denied + pending). Equals totalReceived above — every building lands in exactly one slice.
  const totalReferrals = acceptedCount + deniedCount + pendingCount;

  const statusCounts: Record<InboundStatus, number> = {
    accepted: acceptedCount,
    denied: deniedCount,
    pending: pendingCount,
  };

  return (
    <HighlightsCard>
      <InboundReferralHighlightsHeader />
      <ContentRow>
        <InboundReferralStatusDonutChart
          statusCounts={statusCounts}
          totalReferrals={totalReferrals}
          loading={loading}
        />
        <InboundReferralHighlightMetrics
          acceptanceRate={acceptanceRate}
          denialRate={denialRate}
          selectedRate={selectedRate}
          notSelectedRate={notSelectedRate}
          admittedRate={admittedRate}
          avgTimeToDecisionSeconds={avgTimeToDecisionSeconds}
          totalReceived={totalReceived}
          acceptedCount={acceptedCount}
          deniedCount={deniedCount}
          selectedCount={selectedCount}
          notSelectedCount={notSelectedCount}
          admittedCount={admittedCount}
          closedCount={closedCount}
          decidedCount={decidedCount}
          loading={loading}
        />
      </ContentRow>
    </HighlightsCard>
  );
}

const HighlightsCard = styled(Card)`
  border-radius: 4px;
  padding: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 1200px) {
    height: auto;
  }
`;

const ContentRow = styled.div`
  flex: 1;
  display: flex;
  align-items: stretch;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;
